import { compareAsc, eachWeekOfInterval, endOfWeek } from 'date-fns';

import { sortOrderWithDefault } from '../../coaching-observation/experimental.grid';
import { createReducer } from '../../store.model';

import { ColumnType } from './coaching-coverage-grid.model';
import {
    ActionType,
    GridShiftPeriodDateRangeAction,
    GridSortAction,
    GridTogglePeriodColumnAction,
    GridTogglePeriodDateRangeSelectAction,
    GridToggleRowExpandAction,
    GridToggleScheduleForUser,
    GridToggleShowScheduleItemsListAction,
    RootChangeRosterType,
    RootReloadAction,
    RootReloadResultAction,
    RootSetPeriodRange,
    RootShiftPeriodRange,
    RootToggleCreateEvent,
    RootTogglePeriodRangeSelect,
} from './coaching-coverage.action';
import {
    defaultSortDef,
    defaultState,
    RootState,
} from './coaching-coverage.state';
import { dateRangeForIndex } from './function/date-range-for-index.function';
import { makeColumns } from './function/make-columns.function';
import { makeRows } from './function/make-rows.function';
import { padMissingData } from './function/pad-missing-data.function';
import { togglePeriodColumns } from './function/toggle-period-columns.function';
import { updateRows } from './function/update-rows.function';

const { builder, reducer } = createReducer<ActionType, RootState>(
    defaultState(),
);

builder
    .add<RootReloadAction>(
        ActionType.COACHING_COVERAGE__ROOT__RELOAD,
        (state) => {
            return {
                ...state,
                pending: {
                    ...state.pending,
                    reload: true,
                },
                gridColumns: null,
                gridNeedsLayout: Date.now(),
            };
        },
    )
    .add<RootReloadResultAction>(
        ActionType.COACHING_COVERAGE__ROOT__RELOAD_RESULT,
        (state, action) => {
            const nextPeriodDateRanges = eachWeekOfInterval({
                start: action.payload.start,
                end: action.payload.end,
            }).map((e) => ({
                startDate: e,
                endDate: endOfWeek(e),
            }));

            const nextData = padMissingData(
                action.payload.roster,
                nextPeriodDateRanges,
                action.payload.coverage,
            );
            const nextSelectedDateRange = nextPeriodDateRanges[0];

            /// // separator /////

            const nextScheduleEvents = [...action.payload.scheduleEvents].sort(
                (a, b) => compareAsc(a.eventDate, b.eventDate),
            );

            /// // separator /////

            const nextGridColumns = makeColumns(
                action.payload.roster,
                nextPeriodDateRanges,
            );
            const nextGridRows = makeRows(
                action.payload.roster,
                action.payload.rosterType,
                nextData,
                nextPeriodDateRanges,
                nextScheduleEvents,
                null,
            );

            togglePeriodColumns(nextGridColumns, state.maxShown);

            return {
                ...state,
                pending: {
                    ...state.pending,
                    reload: false,
                },
                rosterType: action.payload.rosterType,
                data: nextData,
                periodDateRanges: nextPeriodDateRanges,
                selectedDateRange: nextSelectedDateRange,
                periodCanGoLeft: false,
                periodCanGoRight:
                    nextSelectedDateRange && nextPeriodDateRanges.length > 1,
                scheduleEvents: nextScheduleEvents,
                roster: action.payload.roster,
                gridColumns: nextGridColumns,
                gridRows: nextGridRows,
                gridNeedsLayout: Date.now(),
            };
        },
    );

builder.add<RootChangeRosterType>(
    ActionType.COACHING_COVERAGE__ROOT__CHANGE_ROSTER_TYPE,
    (state, action) => {
        const gridRows = makeRows(
            state.roster,
            action.payload.rosterType,
            state.data,
            state.periodDateRanges,
            state.scheduleEvents,
            null,
        );

        return {
            ...state,
            rosterType: action.payload.rosterType,
            gridRows,
            gridNeedsLayout: Date.now(),
        };
    },
);

builder.add<RootToggleCreateEvent>(
    ActionType.COACHING_COVERAGE__ROOT__TOGGLE_CREATE_EVENT,
    (state, action) => {
        return {
            ...state,
            createEventContext: !action.payload.userId
                ? null
                : {
                      userId: action.payload.userId,
                  },
        };
    },
);

builder.add<RootTogglePeriodRangeSelect>(
    ActionType.COACHING_COVERAGE__ROOT__TOGGLE_PERIOD_RANGE_SELECT,
    (state) => {
        return {
            ...state,
            selectPeriodRangeContext: state.selectPeriodRangeContext
                ? null
                : {
                      selection: state.maxShown,
                  },
        };
    },
);

builder.add<RootSetPeriodRange>(
    ActionType.COACHING_COVERAGE__ROOT__SET_PERIOD_RANGE,
    (state, action) => {
        const { dateRange, periodCanGoLeft, periodCanGoRight } =
            dateRangeForIndex(
                state.periodDateRanges,
                state.selectedDateRange,
                0,
                action.payload.maxShown,
            );

        togglePeriodColumns(state.gridColumns, action.payload.maxShown);

        // when about to show more columns and (possibly) already at ranges end
        if (action.payload.maxShown > state.maxShown) {
            updateRows(
                state.rosterType,
                state.data,
                state.periodDateRanges,
                state.scheduleEvents,
                state.gridColumns,
                state.gridRows,
                dateRange,
            );
        }

        return {
            ...state,
            selectedDateRange: dateRange,
            maxShown: action.payload.maxShown,
            periodCanGoLeft,
            periodCanGoRight,
            gridNeedsLayout: Date.now(),
        };
    },
);

builder.add<RootShiftPeriodRange>(
    ActionType.COACHING_COVERAGE__ROOT__SHIFT_PERIOD_RANGE,
    (state, action) => {
        const { dateRange, periodCanGoLeft, periodCanGoRight } =
            dateRangeForIndex(
                state.periodDateRanges,
                state.selectedDateRange,
                (action.payload.forward ? 1 : -1) * state.maxShown,
                state.maxShown,
            );

        updateRows(
            state.rosterType,
            state.data,
            state.periodDateRanges,
            state.scheduleEvents,
            state.gridColumns,
            state.gridRows,
            dateRange,
        );

        return {
            ...state,
            selectedDateRange: dateRange,
            periodCanGoLeft,
            periodCanGoRight,
            gridNeedsLayout: Date.now(),
        };
    },
);

builder.add<GridToggleScheduleForUser>(
    ActionType.COACHING_COVERAGE__GRID__TOGGLE_SCHEDULE_FOR_USER,
    (state, action) => {
        return {
            ...state,
            scheduleForUserContext: !action.payload.user
                ? null
                : {
                      managerId: action.payload.managerId,
                      performerId: action.payload.userId,
                      performerUser: action.payload.user,
                  },
        };
    },
);

builder.add<GridSortAction>(
    ActionType.COACHING_COVERAGE__GRID__SORT,
    (state, action) => {
        const gridSortDefs = sortOrderWithDefault(
            state.gridColumns,
            action.payload.column,
            // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
            state.gridSortDefs && state.gridSortDefs.length
                ? state.gridSortDefs[0]
                : null,
            defaultSortDef(),
        );

        return {
            ...state,
            gridSortDefs,
            gridNeedsLayout: Date.now(),
        };
    },
);

builder.add<GridTogglePeriodColumnAction>(
    ActionType.COACHING_COVERAGE__GRID__TOGGLE_PERIOD_COLUMN,
    (state, action) => {
        const col1 = ColumnType.OBSERVATION_COVERAGE_PERCENT.toString();
        const col2 = ColumnType.COMMITMENT_COVERAGE_PERCENT.toString();
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        let next = false;
        const columns = action.payload.column.children.filter(
            (e) => e.id.startsWith(col1) || e.id.startsWith(col2),
        );
        columns.forEach((e) => {
            e.hidden = !e.hidden;
            next = e.hidden; // this is ok since both columns should be in sync
            action.payload.column.ctx.isExpanded = e.hidden; // this is ok since both columns should be in sync
        });

        return {
            ...state,
            gridColumnsNeedResize: [action.payload.column.id],
            gridNeedsLayout: Date.now(),
        };
    },
);

builder.add<GridToggleRowExpandAction>(
    ActionType.COACHING_COVERAGE__GRID__TOGGLE_ROW_EXPAND,
    (state, action) => {
        // eslint-disable-next-line no-return-assign
        action.payload.row.children.forEach(
            (e) => (e.hidden = action.payload.row.ctx.isExpanded),
        );
        action.payload.row.ctx.isExpanded = !action.payload.row.ctx.isExpanded;

        return {
            ...state,
            gridNeedsLayout: Date.now(),
        };
    },
);

builder.add<GridTogglePeriodDateRangeSelectAction>(
    ActionType.COACHING_COVERAGE__GRID__TOGGLE_PERIOD_DATE_RANGE_SELECT,
    (state, action) => {
        return {
            ...state,
            selectPeriodDateRangeContext: !action.payload.column
                ? null
                : {
                      column: action.payload.column,
                      elementSelector: action.payload.elementSelector,
                      dateRanges: state.periodDateRanges,
                      selection: state.periodDateRanges.find((e) => {
                          return (
                              e.startDate.getTime() ===
                                  action.payload.column.ctx.week.startDate.getTime() &&
                              e.endDate.getTime() ===
                                  action.payload.column.ctx.week.endDate.getTime()
                          );
                      }),
                  },
        };
    },
);

builder.add<GridShiftPeriodDateRangeAction>(
    ActionType.COACHING_COVERAGE__GRID__SHIFT_PERIOD_DATE_RANGE,
    (state, action) => {
        // TODO revise. don't know yet if all data would of been requested up front already or if there will be a 'numPeriods' api param
        // TODO revise. to avoid a grid rerender, just update the cell ctxs and force a layout

        // get the offset dateRange based on the column in this action
        // assuming period columns are in asc date order
        // assuming no errors
        /* const firstCIndex = myState.gridColumns.findIndex(e => e.id.startsWith(ColumnType.PERIOD.toString()));
        const cIndex = myState.gridColumns.findIndex(e => e === action.column);
        const drIndex = myState.periodDateRanges.findIndex(e => e === action.dateRange);
        let index = Math.max(drIndex - (cIndex - firstCIndex), 0);
        if (myState.periodDateRanges.length >= 2 && index === myState.periodDateRanges.length - 1) {
            index -= 1;
        }
        const dateRange = myState.periodDateRanges[index];
        const periodCanGoLeft = index > 0;
        const periodCanGoRight = index < myState.periodDateRanges.length - 1; */

        const periodColumns = state.gridColumns.filter((e) =>
            e.id.startsWith(ColumnType.PERIOD.toString()),
        );
        const cIndex = periodColumns.findIndex(
            (e) => e === action.payload.column,
        );
        const { dateRange, periodCanGoLeft, periodCanGoRight } =
            dateRangeForIndex(
                state.periodDateRanges,
                action.payload.dateRange,
                -cIndex,
                state.maxShown,
            );

        updateRows(
            state.rosterType,
            state.data,
            state.periodDateRanges,
            state.scheduleEvents,
            state.gridColumns,
            state.gridRows,
            dateRange,
        );

        return {
            ...state,
            selectedDateRange: dateRange,
            periodCanGoLeft,
            periodCanGoRight,
            gridNeedsLayout: Date.now(),
        };
    },
);

builder.add<GridToggleShowScheduleItemsListAction>(
    ActionType.COACHING_COVERAGE__GRID__TOGGLE_SHOW_SCHEDULE_ITEMS_LIST,
    (state, action) => {
        return {
            ...state,
            scheduleItemsListContext: !action.payload.cell
                ? null
                : {
                      cell: action.payload.cell,
                      type: action.payload.eventType,
                      elementSelector: action.payload.elementSelector,
                      schedule: action.payload.cell.ctx.schedule
                          .filter(
                              (e) => e.eventTypeId === action.payload.eventType,
                          )
                          .slice(2), // maybe have the start num as an action param
                  },
        };
    },
);

export default {
    coachingCoverage: reducer,
};
